import React, { useLayoutEffect, useRef } from "react";
import {
  Briefcase,
  Check,
  CheckCircle2,
  Clock,
  GitMerge,
  Mail,
  User,
} from "lucide-react";
import { WORKFLOW_OVERVIEW_STEPS } from "./workflowOverviewSteps";
import {
  easeTransitMap,
  TRANSIT_MAP_PHASES,
} from "./transitMapMachine";

const ICONS = {
  mail: Mail,
  briefcase: Briefcase,
  clock: Clock,
  user: User,
  check: CheckCircle2,
  nodes: GitMerge,
};

const STATIONS = [
  {
    x: 125,
    y: 145,
    left: "12.5%",
    top: "20.7%",
    position: "below",
    synopsisPosition: "bottom-center",
  },
  {
    x: 355,
    y: 145,
    left: "35.5%",
    top: "20.7%",
    position: "above",
    synopsisPosition: "right-center",
  },
  {
    x: 520,
    y: 305,
    left: "52%",
    top: "43.6%",
    position: "left",
    synopsisPosition: "bottom-close",
  },
  {
    x: 810,
    y: 305,
    left: "81%",
    top: "43.6%",
    position: "below",
    synopsisPosition: "upper-center-spaced",
  },
  {
    x: 690,
    y: 535,
    left: "69%",
    top: "76.4%",
    position: "below",
    synopsisPosition: "right-center",
  },
  {
    x: 300,
    y: 535,
    left: "30%",
    top: "76.4%",
    position: "left",
    synopsisPosition: "upper-right",
  },
];

const STAGE_SYNOPSES = [
  "An incoming email enters the portal and begins the workflow.",
  "The new work item is classified and placed in the shared queue.",
  "The scheduler identifies eligible work and assigns it automatically.",
  "The assigned request becomes available to the Operations team.",
  "Operations validates the request and confirms it is ready to route.",
  "The verified work item is routed to Underwriting for review.",
];

const ROUTE_SEGMENTS = [
  "M 125 145 L 355 145",
  "M 355 145 C 420 145, 455 220, 520 305",
  "M 520 305 L 810 305",
  "M 810 305 C 850 350, 790 455, 690 535",
  "M 690 535 L 300 535",
];

const NEXT_OPERATION = {
  x: 540,
  y: 620,
  left: "54%",
  top: "88.6%",
};

const UNDERWRITING_DECISION = {
  x: 540,
  y: 535,
  left: "54%",
  top: "76.4%",
};

const NO_UNDERWRITING_ROUTE =
  "M 690 535 L 540 535 L 540 620";
const UNDERWRITING_COMPLETE_ROUTE =
  "M 300 535 L 300 595 Q 300 620 325 620 L 540 620";

const DECISION_SYNOPSIS =
  "Is Underwriting review required? YES routes it for review; NO completes it.";
const UNDERWRITING_SYNOPSIS =
  "Underwriting verifies the routed work item before completion.";

function fallbackPoint(segmentIndex, progress) {
  const start = STATIONS[segmentIndex];
  const end = STATIONS[segmentIndex + 1];
  return {
    x: start.x + (end.x - start.x) * progress,
    y: start.y + (end.y - start.y) * progress,
  };
}

function setVehiclePoint(element, point) {
  if (!element) {
    return;
  }
  element.style.left = `${point.x / 10}%`;
  element.style.top = `${point.y / 7}%`;
}

function pointOnRoute(path, progress, start, end) {
  if (
    path &&
    typeof path.getTotalLength === "function" &&
    typeof path.getPointAtLength === "function"
  ) {
    return path.getPointAtLength(path.getTotalLength() * progress);
  }
  return {
    x: start.x + (end.x - start.x) * progress,
    y: start.y + (end.y - start.y) * progress,
  };
}

export default function TransitMapCanvas({
  phase,
  stationIndex,
  phaseProgress,
  stationStatus,
  workItemId,
}) {
  const routeRefs = useRef([]);
  const vehicleRef = useRef(null);
  const noRouteRef = useRef(null);
  const finalRouteRef = useRef(null);
  const yesVehicleRef = useRef(null);
  const noVehicleRef = useRef(null);
  const moving = phase === TRANSIT_MAP_PHASES.IN_TRANSIT;
  const branchMoving = phase === TRANSIT_MAP_PHASES.BRANCH_TRAVEL;
  const finalizingYes = phase === TRANSIT_MAP_PHASES.FINALIZE_YES;
  const underwritingReview =
    phase === TRANSIT_MAP_PHASES.UNDERWRITING_REVIEW;
  const branchStarted = [
    TRANSIT_MAP_PHASES.DECISION,
    TRANSIT_MAP_PHASES.BRANCH_TRAVEL,
    TRANSIT_MAP_PHASES.UNDERWRITING_REVIEW,
    TRANSIT_MAP_PHASES.FINALIZE_YES,
    TRANSIT_MAP_PHASES.COMPLETE,
  ].includes(phase);
  const branchVehiclesVisible =
    branchMoving ||
    underwritingReview ||
    finalizingYes ||
    phase === TRANSIT_MAP_PHASES.COMPLETE;
  const travelProgress = moving ? easeTransitMap(phaseProgress) : 0;
  const branchProgress = branchMoving ? easeTransitMap(phaseProgress) : 0;
  const finalProgress = finalizingYes ? easeTransitMap(phaseProgress) : 0;
  const vehicleVisible =
    phase !== TRANSIT_MAP_PHASES.SETTLING &&
    stationIndex >= 1 &&
    !branchVehiclesVisible &&
    phase !== TRANSIT_MAP_PHASES.COMPLETE;

  useLayoutEffect(() => {
    if (!vehicleRef.current || !vehicleVisible) {
      return;
    }

    let point = STATIONS[stationIndex];
    if (moving) {
      const path = routeRefs.current[stationIndex];
      if (
        path &&
        typeof path.getTotalLength === "function" &&
        typeof path.getPointAtLength === "function"
      ) {
        const length = path.getTotalLength();
        point = path.getPointAtLength(length * travelProgress);
      } else {
        point = fallbackPoint(stationIndex, travelProgress);
      }
    }

    vehicleRef.current.style.left = `${point.x / 10}%`;
    vehicleRef.current.style.top = `${point.y / 7}%`;
  }, [moving, stationIndex, travelProgress, vehicleVisible]);

  useLayoutEffect(() => {
    if (!branchVehiclesVisible) {
      return;
    }

    let yesPoint = STATIONS[5];
    let noPoint = NEXT_OPERATION;

    if (branchMoving) {
      yesPoint = pointOnRoute(
        routeRefs.current[4],
        branchProgress,
        STATIONS[4],
        STATIONS[5],
      );
      noPoint = pointOnRoute(
        noRouteRef.current,
        branchProgress,
        STATIONS[4],
        NEXT_OPERATION,
      );
    } else if (finalizingYes) {
      yesPoint = pointOnRoute(
        finalRouteRef.current,
        finalProgress,
        STATIONS[5],
        NEXT_OPERATION,
      );
    } else if (phase === TRANSIT_MAP_PHASES.COMPLETE) {
      yesPoint = NEXT_OPERATION;
    }

    setVehiclePoint(yesVehicleRef.current, yesPoint);
    setVehiclePoint(noVehicleRef.current, noPoint);
  }, [
    branchMoving,
    branchProgress,
    branchVehiclesVisible,
    finalizingYes,
    finalProgress,
    phase,
  ]);

  return (
    <section className="transit-map" aria-label="Workflow transit map">
      <div className="transit-mapMeta">
        <span><i /> WORKFLOW LINE</span>
        <b>W-01</b>
      </div>

      <svg
        className="transit-mapSvg"
        viewBox="0 0 1000 700"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <marker
            id="transit-direction"
            markerWidth="7"
            markerHeight="7"
            refX="6"
            refY="3.5"
            orient="auto"
          >
            <path d="M 0 0 L 7 3.5 L 0 7 z" className="transit-direction" />
          </marker>
        </defs>

        <path className="transit-mapBoundary" d="M 70 70 H 930 V 675 H 70 Z" />
        <path className="transit-mapGuide" d="M 70 230 H 930 M 70 445 H 930" />

        {ROUTE_SEGMENTS.map((route, index) => {
          const completed =
            index < 4
              ? index < stationIndex
              : underwritingReview ||
                finalizingYes ||
                phase === TRANSIT_MAP_PHASES.COMPLETE;
          const active =
            (moving && index === stationIndex) ||
            (branchMoving && index === 4);
          const activeProgress =
            branchMoving && index === 4 ? branchProgress : travelProgress;

          return (
            <g key={route}>
              <path className="transit-routeCasing" d={route} />
              <path
                ref={(node) => {
                  routeRefs.current[index] = node;
                }}
                className="transit-routeBase"
                d={route}
                markerEnd="url(#transit-direction)"
              />
              <path
                className={`transit-routeProgress${active ? " is-moving" : ""}`}
                d={route}
                pathLength="1"
                style={{
                  strokeDashoffset: completed
                    ? 0
                    : active
                      ? 1 - activeProgress
                      : 1,
                }}
              />
            </g>
          );
        })}

        <g className="transit-conditionalRoute is-visible">
          <path className="transit-routeCasing" d={NO_UNDERWRITING_ROUTE} />
          <path
            ref={noRouteRef}
            className="transit-routeBase transit-routeBase--conditional"
            d={NO_UNDERWRITING_ROUTE}
          />
          <path
            className="transit-routeProgress transit-routeProgress--no"
            d={NO_UNDERWRITING_ROUTE}
            pathLength="1"
            style={{
              strokeDashoffset:
                underwritingReview ||
                finalizingYes ||
                phase === TRANSIT_MAP_PHASES.COMPLETE
                  ? 0
                  : branchMoving
                    ? 1 - branchProgress
                    : 1,
            }}
          />

          <path
            className="transit-routeCasing"
            d={UNDERWRITING_COMPLETE_ROUTE}
          />
          <path
            ref={finalRouteRef}
            className="transit-routeBase transit-routeBase--conditional"
            d={UNDERWRITING_COMPLETE_ROUTE}
          />
          <path
            className="transit-routeProgress transit-routeProgress--yes"
            d={UNDERWRITING_COMPLETE_ROUTE}
            pathLength="1"
            style={{
              strokeDashoffset:
                phase === TRANSIT_MAP_PHASES.COMPLETE
                  ? 0
                  : finalizingYes
                    ? 1 - finalProgress
                    : 1,
            }}
          />
        </g>
      </svg>

      <div
        className={`transit-decisionNode${
          !branchStarted
            ? " is-future"
            : phase === TRANSIT_MAP_PHASES.DECISION || branchMoving
              ? " is-active"
              : " is-complete"
        }`}
        style={{
          left: UNDERWRITING_DECISION.left,
          top: UNDERWRITING_DECISION.top,
        }}
      >
        <span>
          <i>?</i>
        </span>
        <b>Underwriting required?</b>
      </div>
      <span
        className={`transit-branchLabel transit-branchLabel--yes${
          branchStarted ? "" : " is-future"
        }`}
      >
        Yes
      </span>
      <span
        className={`transit-branchLabel transit-branchLabel--no${
          branchStarted ? "" : " is-future"
        }`}
      >
        No
      </span>

      {vehicleVisible ? (
        <div
          ref={vehicleRef}
          className={`transit-vehicle transit-vehicle--${STATIONS[stationIndex].position}${moving ? " is-moving" : ""}`}
          style={{
            left: STATIONS[stationIndex].left,
            top: STATIONS[stationIndex].top,
          }}
          data-testid="transit-map-vehicle"
        >
          <span className="transit-vehicleTrail" />
          <span className="transit-vehicleBody">
            <i />
            {workItemId}
          </span>
        </div>
      ) : null}

      {branchVehiclesVisible ? (
        <>
          <div
            ref={yesVehicleRef}
            className={`transit-vehicle transit-branchVehicle transit-branchVehicle--yes${
              branchMoving || finalizingYes ? " is-moving" : ""
            }${finalizingYes ? " is-finalizing" : ""}${
              phase === TRANSIT_MAP_PHASES.COMPLETE ||
              (finalizingYes && phaseProgress > 0.82)
                ? " is-arrived"
                : ""
            }`}
            style={{ left: STATIONS[4].left, top: STATIONS[4].top }}
          >
            <span className="transit-vehicleTrail" />
            <span className="transit-vehicleBody">
              <i />
              YES · {workItemId}
            </span>
          </div>
          <div
            ref={noVehicleRef}
            className={`transit-vehicle transit-branchVehicle transit-branchVehicle--no${
              branchMoving ? " is-moving" : ""
            }`}
            style={{ left: STATIONS[4].left, top: STATIONS[4].top }}
          >
            <span className="transit-vehicleTrail" />
            <span className="transit-vehicleBody">
              <i />
              NO · {workItemId}
            </span>
          </div>
        </>
      ) : null}

      {WORKFLOW_OVERVIEW_STEPS.map((step, index) => {
        const Icon = ICONS[step.icon] || Mail;
        const active =
          (phase === TRANSIT_MAP_PHASES.AT_STATION &&
            index === stationIndex) ||
          (phase === TRANSIT_MAP_PHASES.DECISION && index === 4) ||
          (underwritingReview && index === 5);
        const complete =
          phase === TRANSIT_MAP_PHASES.COMPLETE ||
          index < stationIndex ||
          (finalizingYes && index === 5);
        const state = active
          ? " is-active"
          : complete
            ? " is-complete"
            : " is-future";
        const ringProgress = complete
          ? 1
          : active && phase === TRANSIT_MAP_PHASES.AT_STATION
            ? phaseProgress
            : active && underwritingReview
              ? phaseProgress
              : active
                ? 1
                : 0;

        return (
          <article
            key={step.id}
            className={`transit-station transit-station--${STATIONS[index].position}${state}`}
            data-stage={step.id}
            style={{
              left: STATIONS[index].left,
              top: STATIONS[index].top,
              "--station-color": step.color,
            }}
            aria-label={`Station ${index + 1}: ${step.title}`}
            aria-current={active ? "step" : undefined}
          >
            <div className="transit-stationMarker">
              <svg
                className="transit-stationProgress"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <circle
                  className="transit-stationProgressTrack"
                  cx="24"
                  cy="24"
                  r="21"
                />
                <circle
                  className="transit-stationProgressValue"
                  cx="24"
                  cy="24"
                  r="21"
                  pathLength="1"
                  style={{ strokeDashoffset: 1 - ringProgress }}
                />
              </svg>
              {complete ? (
                <Check size={15} strokeWidth={3} />
              ) : (
                <span>{String(index + 1).padStart(2, "0")}</span>
              )}
            </div>
            {active ? (
              <div
                key={`${step.id}-${phase}`}
                className={`transit-synopsis transit-synopsis--${STATIONS[index].synopsisPosition}`}
              >
                <span>Stage synopsis</span>
                <p>
                  {phase === TRANSIT_MAP_PHASES.DECISION
                    ? DECISION_SYNOPSIS
                    : underwritingReview
                      ? UNDERWRITING_SYNOPSIS
                      : STAGE_SYNOPSES[index]}
                </p>
              </div>
            ) : null}
            <div className="transit-stationInfo">
              <span className="transit-stationCode">
                <Icon size={11} />
                Station {String(index + 1).padStart(2, "0")}
              </span>
              <h2>{step.title}</h2>
              <span className="transit-stationStatus">
                <i />
                {active ? stationStatus : complete ? step.status : "Upcoming"}
              </span>
            </div>
          </article>
        );
      })}

      <article
        className={`transit-outcome${
          phase === TRANSIT_MAP_PHASES.COMPLETE
            ? " is-complete"
            : underwritingReview || finalizingYes
              ? " is-partial"
              : branchStarted
                ? " is-visible"
                : " is-future"
        }`}
        style={{ left: NEXT_OPERATION.left, top: NEXT_OPERATION.top }}
        aria-label="Available for next operation"
      >
        <div className="transit-outcomeMarker">
          {phase === TRANSIT_MAP_PHASES.COMPLETE ? (
            <Check size={14} strokeWidth={3} />
          ) : (
            <span />
          )}
        </div>
        <div>
          <b>Next Operation</b>
          <span>
            {phase === TRANSIT_MAP_PHASES.COMPLETE
              ? "Available"
              : underwritingReview || finalizingYes
                ? "NO path complete"
                : "Completion"}
          </span>
        </div>
      </article>
    </section>
  );
}
